import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AnyExtension, Editor } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import { TextSelection } from '@tiptap/pm/state';
import { EditorProvider } from '@tiptap/react';

import { isRichTextEditingPortalTarget } from '@/lib/editor-portal-targets';
import { hasRichTextContent } from '@/lib/rich-text';
import { getDocument } from '@/lib/utils';
import { stripAiHighlightMarks } from './extensions/ai-highlight';
import { PRESERVE_HEADING_LEVEL_ON_EMPTY } from './extensions/preserve-heading-level-on-empty';
import { type EditorFocusPoint, focusEditorAtClientPoint } from './focus-editor-at-point';

const DEBOUNCE_DELAY = 500;
const EMPTY_NON_H1_HEADING_RE = /^\s*<h([2-6])\b/i;

/** Collapse empty TipTap shells to ""; keep empty h2–h6 when headings-only. */
function createNormalizePersistedHtml(keepEmptyHeadingShell: boolean) {
  return (html: string): string => {
    const sanitized = stripAiHighlightMarks(html);
    if (hasRichTextContent(sanitized)) {
      return sanitized;
    }

    if (keepEmptyHeadingShell) {
      const level = sanitized.match(EMPTY_NON_H1_HEADING_RE)?.[1];
      if (level) {
        return `<h${level}></h${level}>`;
      }
    }

    return '';
  };
}

function hasPreserveHeadingLevelExtension(extensions: AnyExtension[]): boolean {
  return extensions.some((extension) => extension.name === PRESERVE_HEADING_LEVEL_ON_EMPTY);
}

export interface BaseEditableContentProps {
  value: string;
  placeholder?: string;
  baseExtensions: AnyExtension[];
  onChangeHtml: (html: string) => void;
  onFlushHtml?: (html: string) => void;
  onlyPersistOnFlush?: boolean;
  autoFocus?: boolean;
  initialFocusPoint?: EditorFocusPoint | null;
  editable?: boolean;
  onFocusChange?: (isFocused: boolean) => void;
  onExitEditing?: () => void;
  editorClassName?: string;
  slotBefore?: ReactNode;
  children?: ReactNode;
  /** Hard cap on plain-text length. Blocks further typing and truncates paste. */
  maxPlainTextLength?: number;
}

/**
 * Base TipTap editor component that handles:
 * - Debounced updates to external persistence callbacks
 * - Content synchronization
 * - Blur handling
 */
export function BaseEditableContent({
  value,
  placeholder = 'Start typing...',
  baseExtensions,
  onChangeHtml,
  onFlushHtml,
  onlyPersistOnFlush = false,
  autoFocus = false,
  initialFocusPoint = null,
  editable = true,
  onFocusChange,
  onExitEditing,
  editorClassName,
  slotBefore,
  children,
  maxPlainTextLength,
}: BaseEditableContentProps) {
  const [editor, setEditor] = useState<Editor | null>(null);
  const didAutoFocusRef = useRef(false);
  const initialFocusPointRef = useRef(initialFocusPoint);
  initialFocusPointRef.current = initialFocusPoint;
  const hasUserEditedRef = useRef(false);
  const editableRef = useRef(editable);
  editableRef.current = editable;

  const normalizePersistedHtml = useMemo(
    () => createNormalizePersistedHtml(hasPreserveHeadingLevelExtension(baseExtensions)),
    [baseExtensions],
  );

  const lastPersistedHtmlRef = useRef(normalizePersistedHtml(value));
  const onExitEditingRef = useRef(onExitEditing);
  const skipBlurFocusChangeRef = useRef(false);

  useEffect(() => {
    onExitEditingRef.current = onExitEditing;
  }, [onExitEditing]);

  const extensions = useMemo(() => {
    return [
      ...baseExtensions.filter((ext) => ext.name !== 'placeholder'),
      Placeholder.configure({
        placeholder,
      }),
    ];
  }, [baseExtensions, placeholder]);

  // Sync external value → editor. Skip when local content differs from lastPersisted
  // (debounce window, onlyPersistOnFlush, toolbar focus). Ownership is content-based,
  // not a timer flag — TipTap content is mount-only; this effect is the controlled path.
  useEffect(() => {
    if (!editor?.view || editor.isDestroyed) {
      return;
    }

    try {
      const next = normalizePersistedHtml(value);
      if (next === lastPersistedHtmlRef.current) {
        return;
      }

      const local = normalizePersistedHtml(editor.getHTML());
      if (local !== lastPersistedHtmlRef.current) {
        return;
      }

      // Post-debounce: lastPersisted can lead the prop until the parent re-renders.
      // Don't clobber in-progress typing if that re-render is still stale.
      if (editor.isFocused && hasUserEditedRef.current) {
        return;
      }

      if (value !== editor.getHTML()) {
        editor.commands.setContent(value, { emitUpdate: false });
      }
      lastPersistedHtmlRef.current = normalizePersistedHtml(editor.getHTML());
    } catch {
      // Editor might be destroyed, ignore
    }
  }, [value, editor, normalizePersistedHtml]);

  const persistChange = useCallback(
    (html: string) => {
      const next = normalizePersistedHtml(html);
      if (next === lastPersistedHtmlRef.current) {
        return;
      }

      lastPersistedHtmlRef.current = next;
      hasUserEditedRef.current = false;
      onChangeHtml(next);
    },
    [onChangeHtml, normalizePersistedHtml],
  );

  const persistFlush = useCallback(
    (html: string) => {
      const next = normalizePersistedHtml(html);
      if (next === lastPersistedHtmlRef.current) {
        return;
      }

      lastPersistedHtmlRef.current = next;
      hasUserEditedRef.current = false;
      (onFlushHtml ?? onChangeHtml)(next);
    },
    [onChangeHtml, onFlushHtml, normalizePersistedHtml],
  );

  // persistFlush/onChangeHtml from parents are often new function identities each render.
  // The unmount cleanup effect must NOT list persistFlush as a dependency — otherwise
  // cleanup runs on every render, flushes HTML to the store, and causes an update loop
  // (e.g. after any page-wide re-render when adding a section).
  const persistFlushRef = useRef(persistFlush);
  persistFlushRef.current = persistFlush;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flush pending update immediately (e.g., on blur)
  const flushUpdate = useCallback(() => {
    if (editor?.view && !editor.isDestroyed) {
      try {
        const content = editor.getHTML();
        persistFlush(content);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } catch {
        // Editor might be destroyed, ignore
      }
    }
  }, [editor, persistFlush]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    if (!editable && hasUserEditedRef.current) {
      flushUpdate();
    }

    editor.setEditable(editable);

    // If editing is turned off before the editor blurs (e.g. outside-click handlers on pointerdown),
    // the blur listener effect cleans up when editable is false, so blur() alone would not run handleBlur.
    // skipBlurFocusChangeRef guards a stray blur event.
    if (!editable && editor.isFocused) {
      onFocusChange?.(false);
      skipBlurFocusChangeRef.current = true;
      editor.commands.blur();
    }
  }, [editable, editor, flushUpdate, onFocusChange]);

  const onUpdate = useCallback(
    ({ editor }: { editor: Editor }) => {
      // Idle/preview TipTap instances must not persist normalization transactions.
      // Don't advance lastPersisted here — EditorProvider can emit updates after
      // editableRef flips false but before the editable→idle flush effect runs.
      if (!editableRef.current) {
        return;
      }

      hasUserEditedRef.current = true;

      if (onlyPersistOnFlush) {
        return;
      }

      const content = editor.getHTML();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        persistChange(content);
        timeoutRef.current = null;
      }, DEBOUNCE_DELAY);
    },
    [onlyPersistOnFlush, persistChange],
  );

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    if (!autoFocus) {
      didAutoFocusRef.current = false;
      return;
    }

    if (didAutoFocusRef.current) {
      return;
    }

    didAutoFocusRef.current = true;
    const point = initialFocusPointRef.current;
    const frame = requestAnimationFrame(() => {
      focusEditorAtClientPoint(editor, point);
    });

    return () => cancelAnimationFrame(frame);
  }, [autoFocus, editor]);

  // Handle focus/blur to keep persistence and parent UI in sync.
  useEffect(() => {
    if (!editor?.view || editor.isDestroyed || !editable) {
      return;
    }

    const handleBlur = (event: FocusEvent) => {
      if (!editor?.view || editor.isDestroyed) {
        return;
      }
      // Focus moved to the format toolbar or its sub-menus — not a real "leave the field".
      // Other portaled UI (e.g. Collection Settings) should blur the editor and hide the toolbar.
      if (isRichTextEditingPortalTarget(event.relatedTarget)) {
        return;
      }
      // Radix submenus are portaled; blur often reports relatedTarget=null while focus moved to the menu.
      if (
        event.relatedTarget == null &&
        isRichTextEditingPortalTarget(getDocument()?.activeElement ?? null)
      ) {
        return;
      }
      if (skipBlurFocusChangeRef.current) {
        skipBlurFocusChangeRef.current = false;
        flushUpdate();
        return;
      }
      // `relatedTarget` is often `null` when focus goes nowhere meaningful (e.g. DevTools,
      // window blur, or browser quirks). We only skip when the next focus node matches
      // `isRichTextEditingPortalTarget`; otherwise treat as a real blur — flush is intended.
      onFocusChange?.(false);
      flushUpdate();
    };

    const editorElement = editor.view.dom;
    const handleFocus = onFocusChange ? () => onFocusChange(true) : null;
    if (handleFocus) {
      editorElement.addEventListener('focus', handleFocus);
    }
    editorElement.addEventListener('blur', handleBlur);

    return () => {
      if (editor?.view && !editor.isDestroyed) {
        if (handleFocus) {
          editorElement.removeEventListener('focus', handleFocus);
        }
        editorElement.removeEventListener('blur', handleBlur);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [editable, editor, flushUpdate, onFocusChange]);

  useEffect(() => {
    return () => {
      if (editor && !editor.isDestroyed) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        // Skip flush for idle/preview instances that never received user edits.
        // TipTap serializes "" as "<p></p>"; flushing that would dirty the page store.
        if (!hasUserEditedRef.current) {
          return;
        }
        try {
          if (editor.view && !editor.isDestroyed) {
            const content = editor.getHTML();
            persistFlushRef.current(content);
          }
        } catch {
          // Editor might be destroyed, ignore
        }
      }
    };
  }, [editor]);

  return (
    <EditorProvider
      extensions={extensions}
      content={value}
      onUpdate={onUpdate}
      onCreate={({ editor: createdEditor }) => {
        lastPersistedHtmlRef.current = normalizePersistedHtml(createdEditor.getHTML());
        setEditor(createdEditor);
      }}
      editable={editable}
      immediatelyRender={false}
      slotBefore={slotBefore}
      editorProps={{
        attributes: {
          class: editorClassName ?? '',
        },
        handleTextInput:
          maxPlainTextLength == null
            ? undefined
            : (view, from, to, text) => {
                const currentLength = view.state.doc.textContent.length;
                const selectionLength = view.state.doc.textBetween(from, to, '\n', '\n').length;
                const projected = currentLength - selectionLength + text.length;
                return projected > maxPlainTextLength;
              },
        handlePaste:
          maxPlainTextLength == null
            ? undefined
            : (view, event) => {
                const pasted = event.clipboardData?.getData('text/plain') ?? '';
                if (!pasted) return false;
                const { from, to } = view.state.selection;
                const currentLength = view.state.doc.textContent.length;
                const selectionLength = view.state.doc.textBetween(from, to, '\n', '\n').length;
                const remaining = maxPlainTextLength - (currentLength - selectionLength);
                if (remaining <= 0) return true;
                if (pasted.length <= remaining) return false;
                event.preventDefault();
                view.dispatch(view.state.tr.insertText(pasted.slice(0, remaining), from, to));
                return true;
              },
        handleKeyDown: (view, event) => {
          if (event.key !== 'Escape' || !editable || !onExitEditingRef.current) {
            return false;
          }
          event.preventDefault();
          event.stopPropagation();
          skipBlurFocusChangeRef.current = true;
          onExitEditingRef.current();
          const { from, to } = view.state.selection;
          if (from !== to) {
            view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, from)));
          }
          view.dom.blur();
          return true;
        },
      }}
    >
      {children}
    </EditorProvider>
  );
}
